/**
 * NotionColumnMenu - Notion-style column dropdown menu with comprehensive filtering and sorting options
 * Enhances the existing table system with a more intuitive, Notion-like interface
 */

import React, { useState, useCallback, useMemo } from 'react';
import { 
  ChevronDown,
  Filter,
  SortAsc,
  SortDesc,
  Eye,
  EyeOff,
  Hash,
  Type,
  Calendar,
  ToggleLeft,
  List,
  Search,
  X,
  Check,
  Plus,
  Minus,
  ArrowUpDown,
  Pin,
  PinOff,
  Palette
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { EnhancedTableColumn } from './EnhancedTableHeader';
import { 
  FilterType, 
  FilterOperator, 
  FilterValue, 
  ColumnFilter,
  OPERATOR_LABELS,
  DEFAULT_OPERATORS,
  createColumnFilter
} from '../../../utils/tableFilters';
import { SortDirection, SortDataType } from '../../../utils/tableSort';

export interface NotionColumnMenuProps {
  column: EnhancedTableColumn;
  hasFilter: boolean;
  filterValue: FilterValue | null;
  sortDirection: SortDirection;
  sortPriority?: number | null;
  isPinned?: boolean;
  isVisible: boolean;
  
  // Data for auto-detection and suggestions
  columnData?: unknown[];
  
  // Event handlers
  onFilterChange: (filter: FilterValue | null) => void;
  onSortChange: (direction: SortDirection, dataType?: SortDataType) => void;
  onVisibilityChange: (visible: boolean) => void;
  onPinChange?: (pinned: boolean) => void;
  onClearFilter: () => void;
  onClearSort: () => void;
  
  // Configuration
  enableFiltering?: boolean;
  enableSorting?: boolean;
  enableVisibilityToggle?: boolean;
  enablePinning?: boolean;
  
  className?: string;
}

/**
 * Auto-detect filter type based on column data
 */
function detectFilterType(data: unknown[]): FilterType {
  if (!data || data.length === 0) return 'text';
  
  const sample = data.slice(0, 100).filter(v => v != null);
  if (sample.length === 0) return 'text';
  
  // Check for numbers
  const numbers = sample.filter(v => typeof v === 'number' || !isNaN(Number(v)));
  if (numbers.length > sample.length * 0.8) return 'number';
  
  // Check for dates
  const dates = sample.filter(v => {
    const str = String(v);
    return !isNaN(Date.parse(str)) && (
      str.includes('-') || str.includes('/') || 
      str.toLowerCase().includes('jan') || str.toLowerCase().includes('feb')
    );
  });
  if (dates.length > sample.length * 0.5) return 'date';
  
  // Check for booleans
  const booleans = sample.filter(v => 
    typeof v === 'boolean' || 
    ['true', 'false', 'yes', 'no', '1', '0'].includes(String(v).toLowerCase())
  );
  if (booleans.length > sample.length * 0.8) return 'boolean';
  
  // Check for select options (limited unique values)
  const unique = [...new Set(sample.map(v => String(v)))];
  if (unique.length <= Math.min(10, sample.length * 0.3)) {
    return unique.length <= 20 ? 'select' : 'multiselect';
  }
  
  return 'text';
}

/**
 * Generate filter options for select/multiselect types
 */
function generateFilterOptions(data: unknown[]) {
  if (!data || data.length === 0) return [];
  
  const unique = [...new Set(data.filter(v => v != null).map(v => String(v)))]
    .sort()
    .slice(0, 50); // Limit to 50 options for performance
  
  return unique.map(value => ({
    label: value,
    value: value
  }));
}

/**
 * Quick filter presets based on data type
 */
function getQuickFilterPresets(filterType: FilterType) {
  switch (filterType) {
    case 'text':
      return [
        { label: 'Is not empty', operator: 'is_not_empty' as FilterOperator },
        { label: 'Is empty', operator: 'is_empty' as FilterOperator },
        { label: 'Contains', operator: 'contains' as FilterOperator },
        { label: 'Does not contain', operator: 'not_contains' as FilterOperator },
      ];
    case 'number':
      return [
        { label: 'Greater than', operator: 'greater_than' as FilterOperator },
        { label: 'Less than', operator: 'less_than' as FilterOperator },
        { label: 'Between', operator: 'between' as FilterOperator },
        { label: 'Is not empty', operator: 'is_not_empty' as FilterOperator },
      ];
    case 'date':
      return [
        { label: 'After', operator: 'greater_than' as FilterOperator },
        { label: 'Before', operator: 'less_than' as FilterOperator },
        { label: 'Between', operator: 'between' as FilterOperator },
        { label: 'Is not empty', operator: 'is_not_empty' as FilterOperator },
      ];
    case 'boolean':
      return [
        { label: 'Is true', operator: 'equals' as FilterOperator, value: true },
        { label: 'Is false', operator: 'equals' as FilterOperator, value: false },
      ];
    default:
      return [
        { label: 'Is not empty', operator: 'is_not_empty' as FilterOperator },
        { label: 'Is empty', operator: 'is_empty' as FilterOperator },
      ];
  }
}

/**
 * Main Notion-style column menu component
 */
export const NotionColumnMenu: React.FC<NotionColumnMenuProps> = ({
  column,
  hasFilter,
  filterValue,
  sortDirection,
  sortPriority,
  isPinned = false,
  isVisible = true,
  columnData,
  onFilterChange,
  onSortChange,
  onVisibilityChange,
  onPinChange,
  onClearFilter,
  onClearSort,
  enableFiltering = true,
  enableSorting = true,
  enableVisibilityToggle = true,
  enablePinning = false,
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterMode, setFilterMode] = useState<'quick' | 'advanced'>('quick');
  const [tempFilterValue, setTempFilterValue] = useState<FilterValue | null>(filterValue);

  // Auto-detect filter type and options
  const detectedFilterType = useMemo(() => {
    return column.filterType || (columnData ? detectFilterType(columnData) : 'text');
  }, [column.filterType, columnData]);

  const filterOptions = useMemo(() => {
    if (column.filterOptions) return column.filterOptions;
    if (['select', 'multiselect'].includes(detectedFilterType) && columnData) {
      return generateFilterOptions(columnData);
    }
    return [];
  }, [column.filterOptions, detectedFilterType, columnData]);

  const quickFilterPresets = useMemo(() => {
    return getQuickFilterPresets(detectedFilterType);
  }, [detectedFilterType]);

  const availableOperators = DEFAULT_OPERATORS[detectedFilterType] || [];

  // Handle quick filter selection
  const handleQuickFilter = useCallback((preset: any) => {
    const filter: FilterValue = {
      value: preset.value !== undefined ? preset.value : '',
      operator: preset.operator,
      type: detectedFilterType
    };
    onFilterChange(filter);
    setIsOpen(false);
  }, [detectedFilterType, onFilterChange]);

  // Apply advanced filter
  const handleApplyFilter = useCallback(() => {
    onFilterChange(tempFilterValue);
    setIsOpen(false);
  }, [tempFilterValue, onFilterChange]);

  // Get filter type icon
  const getFilterTypeIcon = () => {
    switch (detectedFilterType) {
      case 'number':
        return <Hash className="h-4 w-4" />;
      case 'date':
        return <Calendar className="h-4 w-4" />;
      case 'boolean':
        return <ToggleLeft className="h-4 w-4" />;
      case 'select':
      case 'multiselect':
        return <List className="h-4 w-4" />;
      default:
        return <Type className="h-4 w-4" />;
    }
  };

  // Get sort icon
  const getSortIcon = () => {
    switch (sortDirection) {
      case 'asc':
        return <SortAsc className="h-4 w-4" />;
      case 'desc':
        return <SortDesc className="h-4 w-4" />;
      default:
        return <ArrowUpDown className="h-4 w-4" />;
    }
  };

  // Render advanced filter form
  const renderAdvancedFilter = () => {
    return (
      <div className="space-y-4 p-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium">Advanced Filter</h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilterMode('quick')}
          >
            ← Back
          </Button>
        </div>

        <div className="space-y-3">
          {/* Operator selection */}
          {availableOperators.length > 1 && (
            <div className="space-y-2">
              <Label>Condition</Label>
              <Select
                value={tempFilterValue?.operator || availableOperators[0]}
                onValueChange={(operator) => 
                  setTempFilterValue(prev => ({
                    ...prev,
                    operator: operator as FilterOperator,
                    type: detectedFilterType,
                    value: prev?.value || ''
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableOperators.map((operator) => (
                    <SelectItem key={operator} value={operator}>
                      {OPERATOR_LABELS[operator]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Value input */}
          <div className="space-y-2">
            <Label>Value</Label>
            {renderFilterValueInput()}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button onClick={handleApplyFilter} size="sm" className="flex-1">
              Apply Filter
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setIsOpen(false)} 
              size="sm"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Render filter value input based on type
  const renderFilterValueInput = () => {
    if (!tempFilterValue) return null;

    switch (detectedFilterType) {
      case 'text':
        return (
          <Input
            placeholder="Enter text..."
            value={tempFilterValue.value || ''}
            onChange={(e) => setTempFilterValue(prev => ({
              ...prev!,
              value: e.target.value
            }))}
          />
        );

      case 'number':
        if (tempFilterValue.operator === 'between') {
          const rangeValue = Array.isArray(tempFilterValue.value) 
            ? tempFilterValue.value : ['', ''];
          return (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder="Min"
                value={rangeValue[0] || ''}
                onChange={(e) => setTempFilterValue(prev => ({
                  ...prev!,
                  value: [e.target.value, rangeValue[1]]
                }))}
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="number"
                placeholder="Max"
                value={rangeValue[1] || ''}
                onChange={(e) => setTempFilterValue(prev => ({
                  ...prev!,
                  value: [rangeValue[0], e.target.value]
                }))}
              />
            </div>
          );
        }
        return (
          <Input
            type="number"
            placeholder="Enter number..."
            value={tempFilterValue.value || ''}
            onChange={(e) => setTempFilterValue(prev => ({
              ...prev!,
              value: Number(e.target.value)
            }))}
          />
        );

      case 'select':
        return (
          <Select 
            value={tempFilterValue.value || ''} 
            onValueChange={(value) => setTempFilterValue(prev => ({
              ...prev!,
              value
            }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select option..." />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'date':
        if (tempFilterValue.operator === 'between') {
          const rangeValue = Array.isArray(tempFilterValue.value) 
            ? tempFilterValue.value : ['', ''];
          return (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={rangeValue[0] || ''}
                onChange={(e) => setTempFilterValue(prev => ({
                  ...prev!,
                  value: [e.target.value, rangeValue[1]]
                }))}
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={rangeValue[1] || ''}
                onChange={(e) => setTempFilterValue(prev => ({
                  ...prev!,
                  value: [rangeValue[0], e.target.value]
                }))}
              />
            </div>
          );
        }
        return (
          <Input
            type="date"
            value={tempFilterValue.value || ''}
            onChange={(e) => setTempFilterValue(prev => ({
              ...prev!,
              value: e.target.value
            }))}
          />
        );

      default:
        return (
          <Input
            placeholder="Enter value..."
            value={tempFilterValue.value || ''}
            onChange={(e) => setTempFilterValue(prev => ({
              ...prev!,
              value: e.target.value
            }))}
          />
        );
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-6 w-6 p-0 opacity-60 hover:opacity-100 transition-opacity",
            (hasFilter || sortDirection) && "opacity-100 text-primary",
            className
          )}
        >
          <ChevronDown className="h-3 w-3" />
          <span className="sr-only">Column options for {column.label}</span>
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="start" 
        className="w-80"
        onClick={(e) => e.stopPropagation()}
      >
        {filterMode === 'advanced' ? renderAdvancedFilter() : (
          <>
            {/* Header */}
            <DropdownMenuLabel className="flex items-center gap-2 pb-2">
              {getFilterTypeIcon()}
              <span className="font-medium">{column.label}</span>
              {hasFilter && (
                <Badge variant="secondary" className="ml-auto">
                  Filtered
                </Badge>
              )}
              {sortDirection && (
                <Badge variant="outline" className="ml-1">
                  {sortPriority && sortPriority > 1 && `${sortPriority} • `}
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </Badge>
              )}
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            {/* Quick Actions */}
            <div className="p-2 space-y-1">
              
              {/* Sorting options */}
              {enableSorting && (
                <>
                  <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                    Sort
                  </div>
                  <DropdownMenuItem 
                    onClick={() => onSortChange('asc', column.sortDataType)}
                    className="flex items-center gap-2"
                  >
                    <SortAsc className="h-4 w-4" />
                    Sort A → Z
                    {sortDirection === 'asc' && <Check className="h-4 w-4 ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => onSortChange('desc', column.sortDataType)}
                    className="flex items-center gap-2"
                  >
                    <SortDesc className="h-4 w-4" />
                    Sort Z → A
                    {sortDirection === 'desc' && <Check className="h-4 w-4 ml-auto" />}
                  </DropdownMenuItem>
                  {sortDirection && (
                    <DropdownMenuItem 
                      onClick={onClearSort}
                      className="flex items-center gap-2 text-muted-foreground"
                    >
                      <X className="h-4 w-4" />
                      Clear sort
                    </DropdownMenuItem>
                  )}
                </>
              )}

              {/* Filtering options */}
              {enableFiltering && (
                <>
                  {enableSorting && <DropdownMenuSeparator />}
                  <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                    Filter
                  </div>
                  
                  {/* Quick filter presets */}
                  {quickFilterPresets.map((preset, index) => (
                    <DropdownMenuItem
                      key={index}
                      onClick={() => handleQuickFilter(preset)}
                      className="flex items-center gap-2"
                    >
                      <Filter className="h-4 w-4" />
                      {preset.label}
                    </DropdownMenuItem>
                  ))}

                  {/* Advanced filter option */}
                  <DropdownMenuItem
                    onClick={() => setFilterMode('advanced')}
                    className="flex items-center gap-2"
                  >
                    <Search className="h-4 w-4" />
                    Advanced filter...
                  </DropdownMenuItem>

                  {/* Clear filter */}
                  {hasFilter && (
                    <DropdownMenuItem 
                      onClick={onClearFilter}
                      className="flex items-center gap-2 text-muted-foreground"
                    >
                      <X className="h-4 w-4" />
                      Clear filter
                    </DropdownMenuItem>
                  )}
                </>
              )}

              {/* Column options */}
              {(enableVisibilityToggle || enablePinning) && (
                <>
                  <DropdownMenuSeparator />
                  <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                    Column
                  </div>
                  
                  {enablePinning && onPinChange && (
                    <DropdownMenuItem 
                      onClick={() => onPinChange(!isPinned)}
                      className="flex items-center gap-2"
                    >
                      {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                      {isPinned ? 'Unpin column' : 'Pin column'}
                    </DropdownMenuItem>
                  )}
                  
                  {enableVisibilityToggle && (
                    <DropdownMenuItem 
                      onClick={() => onVisibilityChange(!isVisible)}
                      className="flex items-center gap-2"
                    >
                      {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      {isVisible ? 'Hide column' : 'Show column'}
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotionColumnMenu;